'use client';
import { cn } from '@/lib/utils';

import Button from '@/components/atoms/button';
import ImageSB, { ImageSBProps } from '@/components/atoms/image/ImageSB';
import LinkButton from '@/components/atoms/linkButton';
import RichTextFieldSB, {
  RichTextFieldSBProps,
} from '@/components/atoms/richTextField/RichTextFieldSB';
import { Text } from '@/components/atoms/text';
import {
  ToggleCMSProps,
  useCMSToggle,
} from '@/components/atoms/toggle/useCMSToggle';
import { BlockReferenceArray } from '@/components/cms/types/block';
import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';

import PostByLine from '@/features/blog/SideNavigation/PostByLine';
import { SimpleCards } from '@/features/cards/SimpleCards';
import {
  BlockHeader,
  BlockHeaderContent,
} from '@/features/utils/BlockHeaderContent';
import { BlockWrapper } from '@/features/utils/BlockWrapper';
import { useShowMoreLess } from '@/features/utils/useShowMoreLess';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { gradientBorderStyling } from '@/styles/gradient';
import { formatDate } from '@/utils/formatDate';

export type SocialProofProps = {
  header: BlockHeader;
  toggle?: ToggleCMSProps;
  reviews?: BlockReferenceArray<{
    title: string;
    description: RichTextFieldSBProps;
    image: ImageSBProps;
    date?: string;
    url?: string;
    reviewUsername?: string;
    reviewDate?: string;
    isReadMoreVisible?: boolean;
    type: 'g2' | 'trustpilot' | 'awards' | 'industry-experts';
  }>;
};

export function SocialProofStrapi({
  block,
  ...rest
}: BlockComponentModel<SocialProofProps>) {
  const { header, reviews, toggle } = block;
  const { texts } = useTranslation();

  const cards =
    reviews?.data?.map(({ attributes }) => {
      return {
        title: attributes.title,
        description: attributes.description,
        image: attributes.image,
        reviewDate: attributes.reviewDate,
        reviewUsername: attributes.reviewUsername,
        url: attributes.url,
        isReadMoreVisible: attributes.isReadMoreVisible,
      };
    }) || [];

  const { filteredToggleList, toggleNode: ToggleNode } = useCMSToggle(
    cards,
    toggle
  );
  const selectedCards = filteredToggleList || cards;
  const { toggleShowMore, initiallyVisibleItems, buttonText, showAction } =
    useShowMoreLess({
      items: selectedCards,
      initialShowCount: 3,
    });

  function getSignatureFromFullname(fullname: string) {
    if (!fullname) return '';
    const fullnameArray = fullname.split(' ');

    if (fullnameArray.length === 1)
      return fullnameArray[0].charAt(0).toUpperCase();

    return (
      fullname.split(' ')[0].charAt(0).toUpperCase() +
      fullname.split(' ')[1].charAt(0).toUpperCase()
    );
  }

  return (
    <BlockWrapper {...rest} theme={header?.theme?.themeKey}>
      <div data-tracker-section={SECTION_NAMES.SOCIAL_PROOF}>
        <BlockHeaderContent
          {...header}
          topChildren={ToggleNode ? ToggleNode() : undefined}
          classNames={{
            children: 'flex flex-col md:flex-row',
          }}
          renderButtons={
            showAction
              ? [
                  (props) => (
                    <Button
                      onClick={toggleShowMore}
                      key={buttonText + 'social-proof-strapi'}
                      {...props}
                      variant='outlined'
                      color='secondary'
                    >
                      {buttonText}
                    </Button>
                  ),
                ]
              : undefined
          }
        >
          {cards.map(
            (
              {
                title,
                description,
                image,
                reviewDate,
                reviewUsername,
                url,
                isReadMoreVisible,
              },
              index
            ) => (
              <div
                className={cn(
                  'flex-1 sm:w-full',
                  initiallyVisibleItems?.length &&
                    !initiallyVisibleItems.some(
                      (card) => card.title === title
                    ) &&
                    'hide-content'
                )}
                key={title}
              >
                <SimpleCards
                  width='full'
                  url={url}
                  key={index}
                  classNames={{
                    root: cn(
                      'w-full self-stretch p-6 rounded-3xl h-full',
                      'dark:shadow-border-px dark:shadow-neutral-dark-N80',
                      'dark:hover:shadow-none dark:border-none',
                      gradientBorderStyling({
                        borderWidth: 'p-px',
                        borderRadius: 'rounded-3xl',
                        utilities: 'dark:hover:',
                      })
                    ),
                  }}
                >
                  <ImageSB
                    block={image}
                    inner={{ classNames: { container: 'mb-3' } }}
                  />
                  <Text
                    variant='subtitle-l'
                    classNames={{ wrapper: 'mb-4 line-clamp-2' }}
                  >
                    {title}
                  </Text>
                  <div
                    data-tracker-component='description'
                    className='md:min-h-24'
                  >
                    <Text color='neutral-N50'>
                      <div className='line-clamp-3'>
                        <RichTextFieldSB block={description} />{' '}
                      </div>
                      {url && (
                        <LinkButton
                          variant='text'
                          color='primary'
                          underline={false}
                          showRightChevron={isReadMoreVisible}
                          slim
                          href={url}
                        >
                          {isReadMoreVisible
                            ? texts.social_proof.read_more
                            : ''}
                        </LinkButton>
                      )}
                    </Text>
                  </div>
                  {reviewUsername && (
                    <PostByLine
                      author={{
                        name: reviewUsername,
                        image: (
                          <div className='bg-neutral-N50 flex h-9 w-9 items-center justify-center rounded-full'>
                            <Text color='neutral-N00'>
                              {getSignatureFromFullname(reviewUsername)}
                            </Text>
                          </div>
                        ),
                      }}
                      releaseDate={
                        reviewDate
                          ? formatDate(new Date(reviewDate))
                          : undefined
                      }
                      classNames={{
                        releaseDate: 'text-neutral-N50',
                        authorName: 'text-neutral-N50',
                      }}
                    />
                  )}
                </SimpleCards>
              </div>
            )
          )}
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}
